import { FC, ReactNode, useMemo } from 'react';

import IconButton from 'app/components/v2/components/IconButton';
import TooltipIcon, { TooltipIconProps } from 'app/components/v2/components/TooltipIcon';
import { SemanticColorKey } from 'app/components/v2/theme/colors';
import Icon, { IconProps } from 'app/components/v3/components/Icon';

import BannerSecondaryElement, { BannerSecondaryElementProps } from './BannerSecondaryElement';
import Box, { MarginProps } from '../Box';
import LoadingSpinner from '../LoadingSpinner';
import Heading5 from '../Text/Heading5';
import SmallBody from '../Text/SmallBody';

// Local Typings
export type Severity = 'error' | 'info' | 'success' | 'warning';

interface ChildrenProps {
  children: ReactNode;
  message?: never;
}
interface MessageProps {
  children?: never;
  message: string;
}
type ChildrenOrMessageProps = ChildrenProps | MessageProps;
export type BannerProps = ChildrenOrMessageProps & MarginProps & {
  id?: string;
  isLoading?: boolean;
  isStacked?: boolean;
  maxWidth?: number;
  onClose?: () => void;
  onlyBorderBottom?: boolean;
  rounded?: boolean;
  secondaryElement?: BannerSecondaryElementProps;
  severity?: Severity;
  title?: string;
  tooltipProps?: TooltipIconProps['tooltipProps'];
  withBoxShadow?: boolean;
};

// Local Variables
const borderColorMap: Record<Severity, SemanticColorKey> = {
  error: 'notification_danger_border',
  info: 'notification_info_border',
  success: 'notification_success_border',
  warning: 'notification_warning_border',
};
const backgroundColorMap: Record<Severity, SemanticColorKey> = {
  error: 'notification_danger_background',
  info: 'notification_info_background',
  success: 'notification_success_background',
  warning: 'notification_warning_background',
};
const iconMap: Record<Severity, IconProps> = {
  error: {
    color: 'danger',
    name: 'alert-error',
  },
  info: {
    color: 'info',
    name: 'alert-info',
  },
  success: {
    color: 'success',
    name: 'alert-success',
  },
  warning: {
    color: 'warning',
    name: 'alert-warning',
  },
};

// Component Definition
const NotificationPresenter: FC<BannerProps> = ({
  children,
  id,
  isLoading,
  isStacked,
  maxWidth,
  message,
  onClose,
  onlyBorderBottom,
  rounded,
  secondaryElement,
  severity = 'info',
  title,
  tooltipProps,
  withBoxShadow,
  ...marginProps
}) => {
  const content = useMemo(() => {
    if (message) {
      return (
        <SmallBody>
          {message}
        </SmallBody>
      );
    }

    if (children) {
      return (
        <Box fullWidth>
          {children}
        </Box>
      );
    }

    return null;
  },
  [children, message]);

  return (
    <Box
      backgroundColor="card_background"
      borderRadius={rounded ? 8 : 0}
      fullWidth
      id={id}
      maxWidth={maxWidth}
      {...marginProps}
    >
      <Box
        alignItems="flex-start"
        backgroundColor={backgroundColorMap[severity]}
        borderBottom={onlyBorderBottom ? '2px solid' : undefined}
        borderColor={borderColorMap[severity]}
        borderRadius={rounded ? 8 : 0}
        borderWidth={onlyBorderBottom ? 0 : 1}
        className="notification"
        display="flex"
        fullWidth
        maxWidth={maxWidth}
        padding={4}
        withBoxShadow={withBoxShadow}
      >
        <Box
          alignItems="center"
          display="flex"
          fullWidth
          gapX={2}
          gapY={1}
          minHeight={24}
          position="relative"
        >
          <Box
            alignItems="center"
            alignSelf="flex-start"
            display="flex"
            paddingTop={1}
          >
            {tooltipProps
              ? (
                <TooltipIcon
                  tooltipProps={tooltipProps}
                  v3IconProps={{
                    ...iconMap[severity],
                    size: 'xxs',
                  }}
                />
              )
              : (
                <Icon
                  {...iconMap[severity]}
                  size="xxs"
                />
              )}
          </Box>

          <Box
            alignItems={isStacked ? 'flex-start' : 'center'}
            display="flex"
            flexDirection={isStacked ? 'column' : 'row'}
            flexGrow={1}
            flexWrap="wrap"
            gapX={1}
            gapY={1}
          >
            {title && (
              <Heading5>
                {title}
              </Heading5>
            )}

            {content}
          </Box>
        </Box>

        {isLoading && (
          <LoadingSpinner />
        )}

        {!isLoading && secondaryElement && (
          <BannerSecondaryElement {...secondaryElement} />
        )}

        {onClose && (
          <Box marginLeft={4}>
            <IconButton
              buttonSize="sm"
              circle
              iconColor="neutral"
              iconName="close"
              iconSize="xxs"
              onClick={onClose}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NotificationPresenter;
